import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, Bell, CalendarClock, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { formatShortCalendarDate } from '../../utils/dateUtils';
import type { FacilityUser } from '../../services/api/facilityService';
import type { RoleOption } from '../../services/api/onboardingService';
import type { FacilityPageViewer } from './utils/facilityPageTypes';
import ActiveUsersTable from './tables/ActiveUsersTable';
import UserInvitationsTable from './tables/UserInvitationsTable';
import {
    facilityUsersToRows,
    isInvitationRow,
    type UserRow,
} from './tables/userManagementTypes';
import InvitationReminderDialog, {
    type InviteReminderMessage,
} from './InvitationReminderDialog';
import type { EditFacilityUserValues } from './EditUserPermissions';
import UserTableSection from './UserTableSection';

interface FacilityUsersSectionProps {
    users: FacilityUser[];
    roles: RoleOption[];
    viewer: FacilityPageViewer;
    isOnboardingStage: boolean;
    isDeactivated?: boolean;
    trialStartDate?: string | null;
    isTrial?: boolean;
    onInviteUsers: () => void;
    onRoleChange: (userId: string, roleId: string) => Promise<void>;
    onBulkRoleAssign: (userIds: string[], roleId: string) => Promise<void>;
    onSendReminders?: (
        invitations: UserRow[],
        message: InviteReminderMessage
    ) => Promise<boolean | void>;
    onDeleteInvitation?: (invitationId: string) => Promise<boolean | void>;
    onEditUser?: (
        user: UserRow,
        values: EditFacilityUserValues
    ) => Promise<boolean | void>;
    onRevokeAccess?: (user: UserRow) => Promise<boolean | void>;
    onGoToRoles?: () => void;
    departmentId?: string;
}

export default function FacilityUsersSection({
    users,
    roles,
    viewer,
    isOnboardingStage,
    isDeactivated,
    trialStartDate,
    isTrial,
    onInviteUsers,
    onRoleChange,
    onBulkRoleAssign,
    onSendReminders,
    onDeleteInvitation,
    onEditUser,
    onRevokeAccess,
    onGoToRoles,
    departmentId,
}: FacilityUsersSectionProps) {
    const { t } = useTranslation();
    const [reminderInvitations, setReminderInvitations] = useState<UserRow[]>(
        []
    );
    const userRows = useMemo(() => facilityUsersToRows(users), [users]);
    const activeUserRows = useMemo(
        () => userRows.filter((user) => !isInvitationRow(user)),
        [userRows]
    );
    const invitationRows = useMemo(
        () => userRows.filter(isInvitationRow),
        [userRows]
    );
    const showActiveUsers = viewer === 'niAdmin' || !isOnboardingStage;
    const canSendReminders = viewer === 'niAdmin' || !isOnboardingStage;
    const reminderDialogOpen = reminderInvitations.length > 0;

    const openReminderDialog = (invitations: UserRow[]) => {
        if (invitations.length === 0) return;
        setReminderInvitations(invitations);
    };

    const handleSendReminders = async (message: InviteReminderMessage): Promise<boolean> => {
        if (!onSendReminders) return true;
        return (await onSendReminders(reminderInvitations, message)) !== false;
    };

    const invitationActions = (
        <div className="flex flex-wrap items-center justify-end gap-2">
            {canSendReminders && onSendReminders && (
                <Button
                    variant="outline"
                    size="sm"
                    className="h-8 border-sky-300/20 bg-sky-300/5 text-xs text-sky-300 hover:bg-sky-300/10 hover:text-sky-200"
                    onClick={() => openReminderDialog(invitationRows)}
                    disabled={invitationRows.length === 0}
                >
                    <Bell className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                    {t('facilities.users.sendRemindersToAll')}
                </Button>
            )}
            <Button
                size="sm"
                className="h-8 text-xs"
                onClick={onInviteUsers}
                disabled={isDeactivated}
            >
                <Mail className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                {t('facilities.users.inviteUsers')}
            </Button>
        </div>
    );

    return (
        <div className="mt-12">
            <div className="space-y-8">
                {showActiveUsers && (
                    <UserTableSection
                        title={t('facilities.users.activeUsers')}
                        count={activeUserRows.length}
                        description={t('facilities.users.activeUsersDesc')}
                        action={
                            onGoToRoles && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-8 text-xs"
                                    onClick={onGoToRoles}
                                >
                                    <ArrowDown
                                        className="mr-1.5 h-3.5 w-3.5"
                                        aria-hidden="true"
                                    />
                                    {t('facilities.users.goToRoles')}
                                </Button>
                            )
                        }
                    >
                        <ActiveUsersTable
                            users={activeUserRows}
                            roles={roles}
                            departmentId={departmentId}
                            showSearches={!isOnboardingStage}
                            onEditUser={onEditUser}
                            onRevokeAccess={onRevokeAccess}
                            emptyMessage={t('facilities.users.noActiveUsers')}
                        />
                    </UserTableSection>
                )}

                <UserTableSection
                    title={t('facilities.users.invitations')}
                    count={invitationRows.length}
                    description={t('facilities.users.invitationsDesc')}
                    action={invitationActions}
                >
                    <div className="space-y-2">
                        {isOnboardingStage &&
                            trialStartDate &&
                            invitationRows.length > 0 && (
                                <div className="flex items-center gap-2 rounded-md border border-white/10 bg-white/5 px-3 py-2 text-xs text-muted-foreground">
                                    <CalendarClock
                                        className="h-3.5 w-3.5"
                                        aria-hidden="true"
                                    />
                                    <span>
                                        {t(
                                            isTrial === false
                                                ? 'facilities.users.invitesSentOnGoLive'
                                                : 'facilities.users.invitesSentOn',
                                            { date: formatShortCalendarDate(trialStartDate) }
                                        )}
                                    </span>
                                </div>
                            )}
                        <UserInvitationsTable
                            users={invitationRows}
                            roles={roles}
                            tableMode={
                                isOnboardingStage ? 'onboarding' : 'management'
                            }
                            onRoleChange={onRoleChange}
                            onBulkRoleAssign={onBulkRoleAssign}
                            onSendReminder={
                                canSendReminders
                                    ? (user) => openReminderDialog([user])
                                    : undefined
                            }
                            onRemove={onDeleteInvitation}
                            emptyMessage={
                                isOnboardingStage
                                    ? t('facilities.users.emptyOnboarding')
                                    : t('facilities.users.emptyManagement')
                            }
                        />
                    </div>
                </UserTableSection>
            </div>
            <InvitationReminderDialog
                open={reminderDialogOpen}
                invitations={reminderInvitations}
                onOpenChange={(open) => {
                    if (!open) setReminderInvitations([]);
                }}
                onSend={handleSendReminders}
            />
        </div>
    );
}
